import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Activity, AlertOctagon, Timer, Search, ListTree } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { errorText } from '../../lib/errors';
import {
  PageHeader, Tabs, StatCard, Card, CardHeader, CardBody, TrendChart, DataTable, Badge, Input, Select, Drawer, EmptyState, LoadingBlock,
} from '../../ui';
import EmployeeDrawer from '../../features/employees/EmployeeDrawer';

const PAGE_SIZE = 50;
const LEVEL_TONE = { info: 'neutral', warn: 'warning', error: 'danger', fatal: 'danger' };
const CATEGORIES = ['user', 'admin', 'system', 'cron', 'client', 'security', 'ui'];

export default function LogsPage() {
  const { t, fmt } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'events';
  const days = params.get('days') || '7';
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState([]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const range = useMemo(() => {
    const to = new Date();
    return { from: new Date(to.getTime() - Number(days) * 864e5).toISOString(), to: to.toISOString() };
  }, [days]);

  useEffect(() => {
    Promise.all([api.get('/admin/logs/stats'), api.get('/admin/logs/trend', { params: { days: Math.max(Number(days), 7) } })])
      .then(([s, tr]) => { setStats(s.data); setTrend(tr.data || []); })
      .catch((err) => toast.error(errorText(err, t('common.error'))));
  }, [days, t]);

  return (
    <>
      <PageHeader
        title={t('logs.title')}
        description={t('logs.subtitle')}
        actions={
          <Select value={days} onChange={(e) => setParam('days', e.target.value)} className="w-40" aria-label={t('logs.period')}>
            {['1', '7', '30'].map((d) => <option key={d} value={d}>{t(`logs.periods.${d}`)}</option>)}
          </Select>
        }
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label={t('logs.total')} value={stats ? fmt.number(stats.total) : '—'} icon={Activity} />
        <StatCard label={t('logs.errors')} value={stats ? fmt.number(stats.failed) : '—'} icon={AlertOctagon} tone={stats?.failed ? 'danger' : 'neutral'} />
        <StatCard label={t('logs.avgTime')} value={stats ? `${fmt.number(stats.initTime)} ms` : '—'} icon={Timer} />
      </div>

      <Card className="mt-6">
        <CardHeader title={t('logs.trendTitle')} />
        <CardBody>
          <TrendChart
            data={trend.map((r) => ({ date: r.date, hits: r.hits }))}
            series={[{ key: 'hits', label: t('logs.seriesEvents') }]}
            formatValue={(v) => fmt.number(v, { notation: 'compact' })}
            formatDate={(d) => fmt.date(d)}
            days={Math.max(Number(days), 7)}
            height={180}
          />
        </CardBody>
      </Card>

      <Tabs
        className="mb-5 mt-8"
        value={tab}
        onChange={(v) => setParam('tab', v === 'events' ? '' : v)}
        items={[
          { id: 'events', label: t('logs.tabs.events'), icon: ListTree },
          { id: 'errors', label: t('logs.tabs.errors'), icon: AlertOctagon },
          { id: 'performance', label: t('logs.tabs.performance'), icon: Timer },
        ]}
      />

      {tab === 'events' && <EventsTab range={range} />}
      {tab === 'errors' && <ErrorsTab range={range} />}
      {tab === 'performance' && <PerformanceTab range={range} />}
    </>
  );
}

function EventsTab({ range }) {
  const { t, fmt } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [level, setLevel] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [search]);
  useEffect(() => { setPage(1); }, [range, level, category]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/logs', {
        params: {
          page, pageSize: PAGE_SIZE, dateFrom: range.from, dateTo: range.to,
          ...(level ? { level } : {}), ...(category ? { category } : {}), ...(debounced ? { filter: debounced } : {}),
        },
      });
      setRows(res.data.entries || []);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setLoading(false);
    }
  }, [page, range, level, category, debounced, t]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: 'timestamp', header: t('logs.time'), render: (l) => <span className="tabular text-zinc-500" title={fmt.dateTime(l.timestamp)}>{fmt.relative(l.timestamp)}</span> },
    { key: 'level', header: t('logs.level'), render: (l) => <Badge tone={LEVEL_TONE[l.level] ?? 'neutral'}>{t(`logs.levels.${l.level}`)}</Badge> },
    {
      key: 'message',
      header: t('logs.event'),
      render: (l) => (
        <div className="max-w-md">
          <p className="truncate text-zinc-900 dark:text-zinc-100">{l.message}</p>
          <p className="truncate font-mono text-[11px] text-zinc-400">{l.category} · {l.type}</p>
        </div>
      ),
    },
    { key: 'tenant_name', header: t('audit.where'), hideOnMobile: true, render: (l) => l.tenant_name ?? <span className="text-zinc-400">—</span> },
    { key: 'user_id', header: t('logs.user'), hideOnMobile: true, render: (l) => (l.user_id ? <span className="tabular">#{Number(l.user_id)}</span> : '—') },
    { key: 'proc_time', header: 'ms', align: 'right', hideOnMobile: true, render: (l) => <span className="tabular text-zinc-500">{l.proc_time ? fmt.number(l.proc_time) : '—'}</span> },
  ];

  let meta = null;
  try { meta = selected?.meta ? JSON.parse(selected.meta) : null; } catch { meta = selected?.meta; }

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="sm:w-80">
          <Input icon={Search} value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('logs.searchPlaceholder')} aria-label={t('common.search')} />
        </div>
        <Select value={level} onChange={(e) => setLevel(e.target.value)} className="sm:w-44" aria-label={t('logs.level')}>
          <option value="">{t('common.all')}</option>
          {Object.keys(LEVEL_TONE).map((l) => <option key={l} value={l}>{t(`logs.levels.${l}`)}</option>)}
        </Select>
        <Select value={category} onChange={(e) => setCategory(e.target.value)} className="sm:w-48" aria-label={t('logs.category')}>
          <option value="">{t('common.all')}</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{t(`logs.categories.${c}`)}</option>)}
        </Select>
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        onRowClick={setSelected}
        empty={<EmptyState icon={ListTree} title={t('logs.empty')} />}
        pagination={{ page, pages: rows.length === PAGE_SIZE ? page + 1 : page, onPage: setPage }}
      />
      <Drawer open={!!selected} onClose={() => setSelected(null)} title={t('logs.details')} subtitle={selected && fmt.dateTime(selected.timestamp)} width="max-w-xl">
        {selected && (
          <div className="space-y-4 px-6 py-5 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge tone={LEVEL_TONE[selected.level] ?? 'neutral'}>{t(`logs.levels.${selected.level}`)}</Badge>
              <Badge>{selected.category}</Badge>
              <Badge>{selected.type}</Badge>
            </div>
            <p className="whitespace-pre-wrap break-words text-zinc-900 dark:text-zinc-100">{selected.message}</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
              {[
                ['IP', selected.ip],
                [t('logs.user'), selected.user_id ? `#${Number(selected.user_id)}` : null],
                [t('audit.where'), selected.tenant_name],
                ['ms', selected.proc_time || null],
                ['session', selected.session_id],
              ].filter(([, v]) => v).map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-zinc-500">{k}</dt>
                  <dd className="break-all font-mono text-xs text-zinc-800 dark:text-zinc-200">{v}</dd>
                </React.Fragment>
              ))}
            </dl>
            {meta && (
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-400">{t('logs.meta')}</p>
                <pre className="max-h-96 overflow-auto rounded-lg bg-zinc-50 p-3 font-mono text-xs text-zinc-800 custom-scrollbar dark:bg-zinc-950 dark:text-zinc-200">
                  {typeof meta === 'string' ? meta : JSON.stringify(meta, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
}

function ErrorsTab({ range }) {
  const { t, fmt } = useI18n();
  const [data, setData] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    setData(null);
    Promise.all([
      api.get('/admin/logs/top-errors', { params: { ...range, limit: 10 } }),
      api.get('/admin/logs/top-users', { params: { ...range, limit: 10 } }),
    ])
      .then(([e, u]) => setData({ errors: e.data.items || [], users: u.data.items || [] }))
      .catch((err) => toast.error(errorText(err, t('common.error'))));
  }, [range, t]);

  if (!data) return <LoadingBlock />;

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <Card>
        <CardHeader title={t('logs.topErrors')} />
        {data.errors.length ? (
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {data.errors.map((e) => (
              <li key={e.type} className="px-5 py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="truncate font-mono text-sm text-zinc-900 dark:text-zinc-100">{e.type}</p>
                  <Badge tone="danger">{t('logs.occurrences', { count: fmt.number(e.count) })}</Badge>
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-zinc-500">{e.last_message}</p>
              </li>
            ))}
          </ul>
        ) : <EmptyState icon={AlertOctagon} title={t('logs.noErrors')} />}
      </Card>
      <Card>
        <CardHeader title={t('logs.topUsers')} />
        {data.users.length ? (
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {data.users.map((u) => (
              <li key={u.user_id}>
                <button type="button" onClick={() => setUserId(Number(u.user_id))} className="w-full px-5 py-3 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">{u.user_name || u.phone || `#${Number(u.user_id)}`}</p>
                    <Badge tone="danger">{t('logs.occurrences', { count: fmt.number(u.errors) })}</Badge>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-zinc-500">
                    {[u.device_model, u.app_version].filter(Boolean).join(' · ')}{u.last_error_at ? ` · ${fmt.relative(u.last_error_at)}` : ''}
                  </p>
                  {u.last_error_message && <p className="mt-1 line-clamp-1 text-xs text-red-600 dark:text-red-400">{u.last_error_message}</p>}
                </button>
              </li>
            ))}
          </ul>
        ) : <EmptyState icon={AlertOctagon} title={t('logs.noErrors')} />}
      </Card>
      <EmployeeDrawer userId={userId} onClose={() => setUserId(null)} onChanged={() => {}} />
    </div>
  );
}

function PerformanceTab({ range }) {
  const { t, fmt } = useI18n();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    api.get('/admin/logs/slowest', { params: range })
      .then((res) => setRows(res.data.items || []))
      .catch((err) => toast.error(errorText(err, t('common.error'))));
  }, [range, t]);

  const max = Math.max(1, ...(rows || []).map((r) => r.avg_time));
  const columns = [
    {
      key: 'type',
      header: t('logs.event'),
      render: (r) => (
        <div>
          <p className="font-mono text-sm text-zinc-900 dark:text-zinc-100">{r.type}</p>
          <p className="text-xs text-zinc-500">{t(`logs.categories.${r.category}`)}</p>
        </div>
      ),
    },
    { key: 'count', header: t('logs.calls'), sortable: true, align: 'right', render: (r) => <span className="tabular">{fmt.number(r.count)}</span> },
    {
      key: 'avg_time',
      header: t('logs.avg'),
      sortable: true,
      render: (r) => (
        <div className="flex items-center gap-3">
          <div className="h-1.5 w-32 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
            <div className="h-full rounded-full bg-[#2a78d6] dark:bg-[#3987e5]" style={{ width: `${Math.max(3, (r.avg_time / max) * 100)}%` }} />
          </div>
          <span className="tabular text-zinc-900 dark:text-zinc-100">{fmt.number(r.avg_time)} ms</span>
        </div>
      ),
    },
    { key: 'max_time', header: t('logs.max'), sortable: true, align: 'right', render: (r) => <span className="tabular text-zinc-500">{fmt.number(r.max_time)} ms</span> },
  ];

  return (
    <>
      <h2 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('logs.slowest')}</h2>
      <DataTable columns={columns} rows={rows || []} loading={!rows} rowKey={(r) => `${r.category}-${r.type}`} empty={<EmptyState icon={Timer} title={t('common.noData')} />} />
    </>
  );
}
